import type { SorterResult } from "antd/lib/table/interface";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { PromptDataProfileList } from "src/app/features/PromptDataProfile/types/PromptDataProfileList";
import { type ExtendedColumnsType, GenericTable, type ResourceTableProps } from "../../../../../components";
import {
    displayDateTimeValue,
    displayEnumValue,
    displayStatusValue,
} from "../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const PromptDataProfilesTable: FC<ResourceTableProps<PromptDataProfileList>> = ({
    onStateChanged,
    onRowSelectionChange,
    datasource,
    resource,
}) => {
    const { t } = useTranslation();
    const getColumnsConfig = useCallback(
        (sortInfo: SorterResult<PromptDataProfileList>): ExtendedColumnsType<PromptDataProfileList>[] => {
            return [
                {
                    title: t("list.form.id"),
                    key: "id",
                    dataIndex: "id",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: t("promptDataList.name"),
                    key: "name",
                    dataIndex: "name",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "name" ? sortInfo.order : null,
                },
                {
                    title: t("promptDataList.code"),
                    key: "promptDataCode",
                    dataIndex: "promptDataCode",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "promptDataCode" ? sortInfo.order : null,
                },
                {
                    title: t("promptDataList.type"),
                    key: "promptDataType",
                    dataIndex: "promptDataType",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "promptDataType" ? sortInfo.order : null,
                    render: (value) => displayEnumValue(value, "prompt-data-type"),
                },
                {
                    title: t("form.status"),
                    key: "status",
                    dataIndex: "status",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "status" ? sortInfo.order : null,
                    render: (value) => displayStatusValue(value),
                },
                {
                    title: t("list.form.changed"),
                    dataIndex: "changed",
                    key: "changed",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "changed" ? sortInfo.order : null,
                    render: (value) => displayDateTimeValue(value),
                },
            ];
        },
        [t],
    );

    const navigate = useNavigate();
    const onRowClick = (record: PromptDataProfileList) => {
        navigate(`${BASE_PATH}/${ResourceName.PROMPT_DATA_PROFILES}/${FORM_ACTION.EDIT}/${record.id}`);
    };

    return (
        (datasource && (
            <GenericTable<PromptDataProfileList>
                getColumnsConfig={getColumnsConfig}
                datasource={datasource}
                name="PromptDataProfileEntitys_table"
                onSelectionCallback={onRowSelectionChange}
                onStateChanged={onStateChanged}
                onRow={(record: PromptDataProfileList) => {
                    return {
                        onClick: () => onRowClick(record),
                    };
                }}
                resource={resource}
            />
        )) ||
        null
    );
};
